import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Flex from 'components/common/Flex';
import { useAppContext } from 'providers/AppProvider';
import { Card, Button, ButtonGroup } from 'react-bootstrap';
import * as echarts from 'echarts/core';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from 'components/common/ReactEchart';
import { fetchAlgoComp } from 'hooks/CallApi';
import { formatCountStyle2 } from 'components/common/SunnyUtils';
import styled from 'styled-components';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const AlgorithmCompare = () => {
  const { getThemeColor } = useAppContext();
  const encryptionChartRef = useRef(null);
  const {
    config: {
      dashboardDate,
      dashboardDateValue,
      Chart_startdate,
      Chart_enddate,
      isDemo
    }
  } = useAppContext();

  const [chartData, setChartData] = useState({
    algoName: [],
    requestCnt: [],
    requestPer: [],
    totalCnt: 0
  });
  const [view, setView] = useState('cf'); // 'top' or 'bottom'

  const [dateRange, setDateRange] = useState({
    start: Chart_startdate,
    end: Chart_enddate
  });
  useEffect(() => {
    // Chart_startdate 또는 Chart_enddate가 변경될 때 상태 업데이트
    setDateRange({
      start: Chart_startdate,
      end: Chart_enddate
    });
  }, [Chart_startdate, Chart_enddate]);

  useEffect(() => {
    let cancelled = false;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const fetchData = async () => {
      try {
        const data = await fetchAlgoComp({
          period: dashboardDateValue,
          start: dateRange.start,
          end: dateRange.end,
          timeZone: clientTimeZone,
          type: view,
          demo: isDemo
        });
        if (cancelled) return;

        const { algoInfoList = [], totalCnt = 0 } = data.data || {};

        setChartData({
          algoName: algoInfoList.map(item => item.algoName),
          requestCnt: algoInfoList.map(item => item.requestCnt),
          requestPer: algoInfoList.map(item => item.requestPer),
          totalCnt
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching algorithm comparison data:', error);
        setChartData({
          algoName: [],
          requestCnt: [],
          requestPer: [],
          totalCnt: 0
        });
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [dashboardDate, dateRange, view, isDemo]);

  const isDataEmpty = chartData.totalCnt === 0;

  const colorMapping = {
    AES: '#3B82F6',
    ARIA: '#10B981',
    SEED: '#8B5CF6',
    RSA: '#F59E0B'
  };

  const getOption = (titleText, algoName, requestCnt, requestPer) => ({
    legend: {
      orient: 'horizontal',
      bottom: 0,
      left: 'center',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: {
        color: getThemeColor('gray-600'),
        fontSize: 12
      },
      data: algoName // Always show legend items
    },
    series: [
      {
        type: 'pie',
        radius: ['65%', '85%'],
        center: ['50%', '70%'],
        startAngle: 180,
        endAngle: 0,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#FFFFFF',
          borderWidth: 2
        },
        label: {
          show: true,
          position: 'center',
          formatter: isDataEmpty
            ? `{b|${titleText}}\n{c|0건}`
            : `{b|${titleText}}\n{c|${formatCountStyle2(
              chartData.totalCnt
            )}건}`,
          offset: [0, -5],
          rich: {
            b: {
              fontSize: 14,
              lineHeight: 20,
              color: '#64748B',
              fontWeight: '600'
            },
            c: {
              fontSize: 22,
              lineHeight: 28,
              fontWeight: '800',
              color: '#1E293B'
            }
          }
        },
        labelLine: { show: false },
        data: isDataEmpty
          ? [{ value: 1, itemStyle: { color: '#f0f0f0' } }]
          : algoName.map((name, index) => ({
            value: requestCnt[index],
            name,
            itemStyle: {
              color: colorMapping[name] || getThemeColor('primary')
            }
          }))
      }
    ],
    tooltip: isDataEmpty
      ? { show: false } // 빈 데이터일 때는 툴팁 비활성화
      : {
        show: true,
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: getThemeColor('gray-100'),
        borderColor: getThemeColor('gray-300'),
        textStyle: { color: getThemeColor('gray-1100') },
        borderWidth: 1,
        transitionDuration: 0,
        axisPointer: { type: 'none' },
        formatter: ({ name, value, percent }) =>
          `${name}<br/>요청 건수: ${formatCountStyle2(
            value
          )}건<br/>요청 비율: ${percent.toFixed(2)}%`
      }
  });

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: 'none',
          backgroundColor: 'transparent',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap' }}>알고리즘 요청 비교</h5>
          <span
            style={{
              padding: '2px 8px',
              backgroundColor: '#2C7BE5',
              color: 'white',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}
          >
            {dashboardDate}
          </span>
        </div>
        <ButtonGroup>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'cf' ? 'primary' : 'outline-primary'}
            onClick={() => setView('cf')}
          >
            CF
          </Button>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'api' ? 'primary' : 'outline-primary'}
            onClick={() => setView('api')}
          >
            API
          </Button>
          <Button
            style={{ fontSize: '0.7rem' }}
            variant={view === 'sdk' ? 'primary' : 'outline-primary'}
            onClick={() => setView('sdk')}
          >
            SDK
          </Button>
        </ButtonGroup>
      </Card.Header>
      <Card.Body
        as={Flex}
        className="pt-0"
        alignItems="center"
        justifyContent="space-between"
      >
        <ReactEchart
          echarts={echarts}
          option={getOption(
            '알고리즘',
            chartData.algoName,
            chartData.requestCnt,
            chartData.requestPer
          )}
          notMerge={true}
          ref={encryptionChartRef}
          style={{ width: '100%', height: '15rem' }}
        />
      </Card.Body>
    </Card>
  );
};

AlgorithmCompare.propTypes = {
  totalRequests: PropTypes.number
};

export default AlgorithmCompare;

const CardHeader = styled(Card.Header)`
  display: flex;
  align-items: start;
  justify-content: space-between;
  padding-bottom: 0px;
  gap: 8px;
  flex-wrap: wrap;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 0px;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
